import { useState } from "react";
import { BuzerIcon } from "../../BuzerIcons";
import { Icon } from "../../icons";
import { Glyph, Timer, Modal } from "../../components/ui";
import { Sheet, Button, IconButton, Tag, DescrBox } from "../../components/ui/ds";
import { freqLabel } from "../../lib/format";
import { LIFE_CAT, RECUR_PERIOD_UNIT } from "../../lib/constants";
import { iconChipStyle } from "../../lib/icons-resolve";
import { api } from "../../lib/api";
import { session } from "../../lib/session";

// Reusable task/habit detail sheet ("Detail úlohy"). Built on the Sheet overlay
// primitive; opened by tapping a habit row (and reusable for any task-like item).
// Renders the stats we actually have (points, frequency, 7-day history + weekly
// progress) and the primary actions. All behaviour comes in via props so the
// same component serves habits, challenges, etc.
// tone = odtieň pilulky; ak sa neposkytne, odvodí sa z valueColor
// (var(--brand-primary) → brand, var(--streak) → streak, inak neutral).
export function Stat({ icon, value, valueColor, label, tone }) {
  const t = tone || (valueColor === "var(--brand-primary)" ? "brand" : valueColor === "var(--streak)" ? "streak" : "neutral");
  return (
    <div className={`bz-tile bz-tile--${t}`}>
      <span className="bz-tile__icon">{icon}</span>
      <span className="bz-tile__value" style={valueColor ? { color: valueColor } : undefined}>{value}</span>
      <span className="bz-tile__label">{label}</span>
    </div>
  );
}

// v35 — bublina „prvý (neúplný) týždeň" (Plán A): keď je týždenný cieľ znížený (založené v priebehu
// týždňa / cez víkend), krátko vysvetlí, prečo je nižší a že od pondelka platí plný. Zdieľané detailom
// návyku aj výzvy (rovnaký vzhľad + copy). Nič neukáže, keď cieľ nie je znížený (target ≥ full).
export function FirstWeekNote({ target, full, style }) {
  if (!(target < full)) return null;
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 7, fontSize: 12.5, color: "var(--neutral-text-muted)", lineHeight: 1.45, marginTop: 10, ...style }}>
      <BuzerIcon name="bulb" size={14} color="var(--brand-primary)" style={{ flexShrink: 0, marginTop: 1 }} />
      <span>Tento prvý (neúplný) týždeň stačí <strong>{target}×</strong> — začalo sa v priebehu týždňa, aby to nezačalo zlyhaním. Od pondelka platí plných <strong>{full}×</strong> za týždeň.</span>
    </div>
  );
}

// AI verdikt → slovenský label + odtieň Tagu (zdieľané so zobrazením výsledku nižšie)
const VERDICT_META = {
  great: { label: "Darí sa skvele", tone: "success", icon: "sparkles" },
  ok: { label: "Drží sa", tone: "brand", icon: "target" },
  struggling: { label: "Treba niečo zmeniť", tone: "danger", icon: "wrench" },
};

export function TaskDetail({ task, routineName, lite, onClose, onToggle, onSkip, onEdit, onDelete }) {
  const t = task;
  // font-unify — popis (t.desc) sa presunul zo skracovaného podnadpisu do čitateľného tela (bz-aitext);
  // podnadpis nechávame len na rutinu, aby sa dlhý popis neorezával na jeden riadok.
  const subtitle = routineName || null;
  // v2 — „Zhodnotiť s AI": pošle históriu TOHTO návyku AI a ukáže verdikt + radu čo ďalej
  const [aiEval, setAiEval] = useState(null);  // null | {busy:true} | {error} | {verdict,message,suggestion}
  async function runAiEval() {
    setAiEval({ busy: true });
    try {
      const r = await api("/api/ai/evaluate-habit", { method: "POST", body: { task_id: t.id } });
      setAiEval(r);
    } catch (e) { setAiEval({ error: e.message || "AI je momentálne nedostupné." }); }
  }
  const weekly = t.recur_mode === "weekly_count" || t.recur_mode === "period";  // v33b — „N× za obdobie" má rovnaký postup-riadok
  const periodUnit = t.recur_mode === "period" ? (RECUR_PERIOD_UNIT[t.recur_period] || "obdobie") : "týždeň";
  const isDaily = (t.recur_mode || "daily") === "daily";  // daily habits skip the progress row + rule
  const remain = Math.max(0, (t.week_target || 0) - (t.week_done || 0));
  const n = t.streak || 0;
  // séria v jednotkách obdobia: deň / týždeň / mesiac / pol rok / rok
  const UNIT_SK = {
    week: [n === 1 ? "týždeň" : (n >= 2 && n <= 4 ? "týždne" : "týždňov")],
    month: [n === 1 ? "mesiac" : (n >= 2 && n <= 4 ? "mesiace" : "mesiacov")],
    half: [n === 1 ? "polrok" : (n >= 2 && n <= 4 ? "polroky" : "polrokov")], year: [n === 1 ? "rok" : (n >= 2 && n <= 4 ? "roky" : "rokov")],
  };
  const streakLabel = UNIT_SK[t.streak_unit]
    ? `${n} ${UNIT_SK[t.streak_unit][0]}`
    : `${n} ${n === 1 ? "deň" : (n >= 2 && n <= 4 ? "dni" : "dní")}`;

  return (
    <>
    <Sheet center
      icon={<Glyph icon={t.icon} size={20} fallback="target" />} iconStyle={iconChipStyle(t.icon_color)}
      title={t.label} subtitle={subtitle} onClose={onClose}
      footer={
        <div style={{ display: "flex", flexDirection: "column", gap: 9, width: "100%" }}>
          {t.done ? (
            <Button variant="destructive" block onClick={(e) => { onToggle(e); onClose(); }}><BuzerIcon name="undo" size={16} color="currentColor" />Vrátiť splnenie</Button>
          ) : (
            <>
              {/* f-batch — posiela click event ďalej (onToggle(e)), nech splnenie z detailu
                  dostane ROVNAKÝ rozpŕskavajúci efekt v mieste ťuku ako riadok v zozname. */}
              <Button variant="primary" block onClick={(e) => { onToggle(e); onClose(); }}>
                <Icon name="check" size={19} color="currentColor" stroke={2.6} />{lite ? "Splniť" : `Splniť +${t.pts}`}
              </Button>
              {t.timer_sec > 0 && (
                <Timer variant="button" id={t.id} seconds={t.timer_sec} onComplete={() => { onToggle(); onClose(); }} />
              )}
              {/* v25 §4.4 — preskočiť dnes (ospravedlnený deň — séria sa nepreruší) */}
              {/* v25 §10.8 — zrušenie preskočenia je deštruktívne + potvrdzuje sa (parent otvorí confirm) */}
              {onSkip && (t.skip_today
                ? <Button variant="destructive" block onClick={() => { onSkip(false); onClose(); }}><BuzerIcon name="undo" size={16} color="currentColor" />Zrušiť preskočenie</Button>
                : <Button variant="ghost" block style={{ color: "var(--brand-primary)" }} title="Deň sa ospravedlní — séria sa nepreruší" onClick={() => { onSkip(true); onClose(); }}><BuzerIcon name="wave" size={16} color="currentColor" />Preskočiť dnes</Button>)}{/* v27 §7 — len text vo farbe témy, bez výplne */}
            </>
          )}
          {/* v2 — AI zhodnotí históriu tohto návyku (darí sa/treba upraviť/archivovať?) */}
          {session.ai && (
            <Button variant="ghost" block onClick={runAiEval}>
              <BuzerIcon name="sparkles" size={16} color="currentColor" />Zhodnotiť s AI
            </Button>
          )}
          {/* v2 — Upraviť + circled trash (⋯ menu retired) */}
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="ghost" block onClick={() => { onEdit(); onClose(); }}>
              <Icon name="edit" size={16} color="currentColor" />Upraviť
            </Button>
            {onDelete && <IconButton variant="ghost" aria-label="Vymazať" title="Vymazať" onClick={() => { onDelete(); }} style={{ color: "var(--danger)" }}><Icon name="delete" size={16} color="currentColor" /></IconButton>}
          </div>
        </div>
      }
    >
      {/* font-unify → popis návyku v rovnakom ráme ako pri výzve/súťaži (zdieľaný DescrBox) */}
      <DescrBox text={t.desc} style={{ marginBottom: 10 }} />
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {!lite && <Stat icon={<span style={{ fontFamily: "var(--ff-mono)", fontWeight: 700, fontSize: 17, lineHeight: 1, color: "var(--brand-primary)" }}>+</span>} value={t.pts} valueColor="var(--brand-primary)" label="BODY" />}
        <Stat icon={<BuzerIcon name="calendar" size={17} color="currentColor" />} value={freqLabel(t)} label="FREKV." />
        {/* v33h — Lite (bez bodov) má sériu vo farbe témy, nie streak-oranžovú */}
        <Stat icon={<BuzerIcon name="flame" size={17} color={lite ? "var(--brand-primary)" : "var(--streak)"} />} value={streakLabel} valueColor={lite ? "var(--brand-primary)" : "var(--streak)"} label="SÉRIA" />
        {/* v32 — štvrtá dlaždica: životná kategória */}
        {t.category && LIFE_CAT[t.category] && (
          <Stat icon={<BuzerIcon name={LIFE_CAT[t.category].icon} size={17} color="currentColor" />} value={LIFE_CAT[t.category].label} label="KATEGÓRIA" />
        )}
        {t.days_left != null && <Stat icon={<BuzerIcon name="hourglass" size={17} color="currentColor" />} value={`${t.days_left} dní`} label="AKTÍVNE EŠTE" />}
        {/* v34 — dosiahnutá frekvencia ako pilulka (rovnaký font ako ostatné dlaždice): bodky sú „ikona", N/N je hodnota */}
        {weekly && (
          <Stat
            icon={<span style={{ display: "inline-flex", gap: 4 }}>
              {Array.from({ length: t.week_target || 0 }).map((_, i) => (
                <span key={i} style={{ width: 9, height: 9, borderRadius: "50%", background: i < (t.week_done || 0) ? "var(--brand-primary)" : "transparent", border: i < (t.week_done || 0) ? "none" : "1.5px solid var(--neutral-border-strong)" }} />
              ))}
            </span>}
            value={`${t.week_done || 0}/${t.week_target}`}
            label="splnené"
          />
        )}
      </div>

      {/* v35 — rovnaká „prvý týždeň" bublina ako v detaile výzvy (len pri weekly_count, kde platí
          „N× za týždeň"); pri N× za mesiac/pol rok/rok by copy „od pondelka" nesedelo. */}
      {t.recur_mode === "weekly_count" && <FirstWeekNote target={t.week_target || 0} full={t.recur_n || 0} />}

      {/* postup pri intervalových/týždňových-dňových návykoch (nie „N× za obdobie") — dnešný stav */}
      {!isDaily && !weekly && (
        <div style={{ marginTop: 10, marginBottom: 6, display: "flex", alignItems: "center", gap: 9 }}>
          {t.done ? (<>
            <Icon name="check" size={16} color="var(--success)" stroke={2.4} /><span style={{ fontSize: 13, fontWeight: 700, color: "var(--success)" }}>dnes splnené · séria +1</span>
          </>) : (
            <span style={{ fontSize: 13, color: "var(--neutral-text-muted)" }}>Dnes ešte nesplnené</span>
          )}
        </div>
      )}

      {/* v25 §8 — nastavené pripomenutie návyku */}
      {t.remind_at && (
        <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "var(--neutral-text-muted)" }}>
          <BuzerIcon name="bell" size={14} color="currentColor" />Pripomenutie o {t.remind_at}
        </div>
      )}

    </Sheet>
    {/* v2 — výsledok „Zhodnotiť s AI" (verdikt + zhodnotenie + rada čo ďalej) */}
    {aiEval && (
      <Modal center title="AI hodnotenie" icon="sparkles" maxWidth={400} onClose={() => setAiEval(null)}>
        {aiEval.busy ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 20 }}><div className="spin" /></div>
        ) : aiEval.error ? (
          <div style={{ color: "var(--danger)", fontSize: 13.5 }}>{aiEval.error}</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <Tag tone={(VERDICT_META[aiEval.verdict] || VERDICT_META.ok).tone} icon={(VERDICT_META[aiEval.verdict] || VERDICT_META.ok).icon}>
                {(VERDICT_META[aiEval.verdict] || VERDICT_META.ok).label}
              </Tag>
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--neutral-text-main)" }}>{aiEval.message}</div>
            {aiEval.suggestion && (
              <div style={{ padding: "10px 12px", border: "1px solid var(--neutral-border)", borderRadius: "var(--radius-md)",
                background: "var(--neutral-surface-2)", display: "flex", gap: 8, alignItems: "flex-start" }}>
                <BuzerIcon name="sparkles" size={15} color="var(--brand-primary)" style={{ flexShrink: 0, marginTop: 1 }} />
                <span style={{ fontSize: 13.5, lineHeight: 1.5, color: "var(--neutral-text-main)" }}>{aiEval.suggestion}</span>
              </div>
            )}
          </div>
        )}
      </Modal>
    )}
    </>
  );
}
